import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface AppCarouselProps {
  screenshots?: string[];
}

const AppCarousel: React.FC<AppCarouselProps> = ({ screenshots = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(4);
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleSlides(1);
      } else if (window.innerWidth < 1024) {
        setVisibleSlides(2);
      } else {
        setVisibleSlides(4);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if we should use placeholders
  const hasScreenshots = screenshots.length > 0;
  const totalSlides = hasScreenshots ? screenshots.length : 4;
  
  // Create infinite array of screenshots for the carousel
  const getInfiniteItems = () => {
    if (hasScreenshots) {
      // Repeat the array multiple times for infinite effect
      return [...screenshots, ...screenshots, ...screenshots]; 
    } else {
      // Create placeholders and repeat them
      return Array(12).fill(0); // 4 placeholders × 3 repeats
    }
  };

  const infiniteItems = getInfiniteItems();
  
  // Navigation functions with infinite scrolling
  const prevSlide = () => {
    setCurrentSlide(prev => {
      const newSlide = prev - 1;
      // Reset to end of first repetition when reaching start of visible area
      if (newSlide < 0) {
        return totalSlides - 1;
      }
      return newSlide;
    });
  };

  const nextSlide = () => {
    setCurrentSlide(prev => {
      const newSlide = prev + 1;
      // Reset to start of first repetition when reaching end of visible area
      if (newSlide >= totalSlides) {
        return 0;
      }
      return newSlide;
    });
  };

  return (
    <div className="mb-6">
      {/* Carousel container without background */}
      <div className="relative overflow-hidden">
        {/* Carousel track with infinite effect */}
        <div 
          className="flex transition-transform duration-300 ease-in-out "
          style={{ 
            transform: `translateX(-${(currentSlide * 100) / visibleSlides}%)`,
          }}
        >
          {hasScreenshots ? (
            // Actual screenshots - repeated for infinite effect
            infiniteItems.map((src, index) => (
              <div 
                key={index} 
                className="w-full md:w-1/2 lg:w-1/4 flex-shrink-0 px-1"
              >
                <div className="aspect-w-16 aspect-h-9 flex items-center justify-center h-48 md:h-64 border-1 border-[#C7DEF6]">
                  <img 
                    src={src} 
                    alt={`Application screenshot ${index + 1}`} 
                    className="max-h-full max-w-full object-contain "
                  />
                </div>
              </div>
            ))
          ) : (
            // Placeholder images - repeated for infinite effect
            infiniteItems.map((_, index) => (
              <div 
                key={index} 
                className="w-full md:w-1/2 lg:w-1/4 flex-shrink-0 px-1"
              >
                <div className="aspect-w-16 aspect-h-9 flex items-center justify-center h-48 md:h-64">
                  <img 
                    src="/Frame18.svg" 
                    alt="Default screenshot" 
                    className="h-40 md:h-56 w-auto object-contain"
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Navigation Buttons */}
      <div className="flex justify-end mt-2">
        <button 
          onClick={prevSlide}
          className="bg-[#1a4565] text-white p-1 h-8 w-8 flex items-center justify-center rounded-sm transition-opacity hover:opacity-80"
          aria-label="Previous slide"
        >
          <ChevronLeft size={20} />
        </button>
        <button 
          onClick={nextSlide}
          className="bg-[#1a4565] text-white p-1 h-8 w-8 flex items-center justify-center rounded-sm ml-1 transition-opacity hover:opacity-80"
          aria-label="Next slide"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default AppCarousel;